'use client';

import { useState, useEffect, useRef } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

export const useAnimatedNumber = (
  targetValue: number,
  duration: number = 500,
  enabled: boolean = true,
  decimals: number = 0,
) => {
  // Disabled, it starts on the target: effects never run on the server, so a start at zero
  // would print 0 in the server HTML.
  const [displayValue, setDisplayValue] = useState(enabled ? 0 : targetValue);
  const animationRef = useRef<number | null>(null);
  const displayValueRef = useRef(enabled ? 0 : targetValue);
  // Known only after hydration; when it turns true, the effect below cancels any frame in flight.
  const reducedMotion = usePrefersReducedMotion();
  const animates = enabled && !reducedMotion;

  useEffect(() => {
    displayValueRef.current = displayValue;
  }, [displayValue]);

  useEffect(() => {
    if (!animates) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDisplayValue(targetValue);
      return;
    }

    // Cancelar animación previa si existe
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const startValue = displayValueRef.current;
    const difference = targetValue - startValue;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);

      const currentValue = startValue + difference * easeOut;
      setDisplayValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        animationRef.current = null;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    // Cleanup al desmontar
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetValue, duration, animates]);

  return Number(displayValue.toFixed(decimals));
};
